import type { ArtifactType, ConvertAndCopyFormat } from "~/services/artifacts";

export interface FormatOption {
  value: ConvertAndCopyFormat;
  label: string;
  description: string;
}

// Format options per artifact type for download/export dialogs. Kept narrow
// on purpose — only the formats the convert-and-copy Lambda actually
// produces for that artifact kind. "original" is always last as the
// "as-stored" passthrough.
const OPTIONS: Record<ArtifactType, readonly FormatOption[]> = {
  dataframe: [
    { value: "csv", label: "CSV", description: "Comma-separated values, opens in Excel/Sheets" },
    {
      value: "parquet",
      label: "Parquet",
      description: "Columnar format, efficient for large datasets",
    },
    { value: "html", label: "HTML", description: "HTML table preview" },
    { value: "original", label: "Original", description: "Source format, no conversion" },
  ],
  figure: [
    { value: "html", label: "HTML", description: "Standalone HTML file with interactive chart" },
    { value: "original", label: "Original", description: "Source format, no conversion" },
  ],
  value: [
    { value: "pickle", label: "Pickle", description: "Python pickle file" },
    { value: "original", label: "Original", description: "Source format, no conversion" },
  ],
  text: [{ value: "original", label: "Original", description: "As stored" }],
  blob: [{ value: "original", label: "Original", description: "As stored" }],
  image: [{ value: "original", label: "Original", description: "As stored" }],
};

export const formatOptionsFor = (type: ArtifactType): readonly FormatOption[] => OPTIONS[type];

// Default format we send when the user clicks the primary download button
// without picking from the menu. Matches the artifact's native form so
// nothing is converted unnecessarily.
export const defaultFormatFor = (type: ArtifactType): ConvertAndCopyFormat => {
  switch (type) {
    case "dataframe":
      return "csv";
    case "figure":
      return "html";
    case "value":
      return "pickle";
    default:
      return "original";
  }
};

// Display labels — uppercase extension reads cleaner in menus than the
// raw enum value (which we still send on the wire).
export const formatLabel: Record<ConvertAndCopyFormat, string> = {
  csv: "CSV",
  parquet: "Parquet",
  zip: "ZIP",
  html: "HTML",
  pickle: "Pickle",
  original: "Original",
};
